import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../context/ThemeContext';
import { useToast } from '../../context/ToastContext';
import { timeOffRequestsAPI } from '../../services/api';

interface TimeOffRequest {
  id: number;
  request_type: string;
  start_date: string;
  end_date: string;
  reason: string;
  status: string;
}

interface TimeOffRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  requestToEdit?: TimeOffRequest | null;
  existingRequests?: TimeOffRequest[];
}

const TimeOffRequestModal: React.FC<TimeOffRequestModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  requestToEdit,
  existingRequests,
}) => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const { showToast } = useToast();

  const [formData, setFormData] = useState({
    request_type: 'vacation',
    start_date: '',
    end_date: '',
    reason: '',
  });

  const [submitting, setSubmitting] = useState(false);

  const hasOverlap = (start: Date, end: Date) =>
    existingRequests?.some(r =>
      ['approved', 'pending'].includes(r.status) &&
      new Date(r.end_date) >= start && new Date(r.start_date) <= end
    );

  // Populate form when requestToEdit changes
  React.useEffect(() => {
    if (requestToEdit) {
      setFormData({
        request_type: requestToEdit.request_type,
        start_date: requestToEdit.start_date,
        end_date: requestToEdit.end_date,
        reason: requestToEdit.reason,
      });
    } else {
      setFormData({
        request_type: 'vacation',
        start_date: '',
        end_date: '',
        reason: '',
      });
    }
  }, [requestToEdit, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.start_date || !formData.end_date) {
      showToast('Please select both start and end dates', 'error');
      return;
    }

    if (new Date(formData.end_date) < new Date(formData.start_date)) {
      showToast('End date must be after start date', 'error');
      return;
    }

    const startDate = new Date(formData.start_date);
    const endDate = new Date(formData.end_date);
    const totalDays = (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24) + 1;
    const hoursRequested = totalDays * 8;

    setSubmitting(true);
    try {
      if (requestToEdit) {
        // Update existing request
        await timeOffRequestsAPI.update(requestToEdit.id, {
          ...formData,
          hours_requested: hoursRequested,
        });
        showToast(t('timeOff.updateSuccess') || 'Request updated successfully', 'success');
      } else {
        // Create new request
        await timeOffRequestsAPI.create({
          ...formData,
          hours_requested: hoursRequested,
        });
        showToast(t('timeOff.submitSuccess'), 'success');
      }
      setFormData({ request_type: 'vacation', start_date: '', end_date: '', reason: '' });
      onSuccess();
      onClose();
    } catch (error) {
      showToast(requestToEdit ? t('timeOff.updateFailed') || 'Failed to update request' : t('timeOff.submitFailed'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div
        className={`${isDark ? 'bg-slate-800' : 'bg-white'} rounded-lg p-6 max-w-md w-full mx-4`}
      >
        <h2
          className={`text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}
        >
          {requestToEdit ? t('timeOff.editRequest') || 'Edit Time Off Request' : t('timeOff.requestTimeOff')}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Leave Type */}
          <div>
            <label
              className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}
            >
              {t('timeOff.typeOfLeave')}
            </label>
            <select
              value={formData.request_type}
              onChange={(e) =>
                setFormData({ ...formData, request_type: e.target.value })
              }
              className={`w-full px-3 py-2 border rounded-lg ${
                isDark
                  ? 'bg-slate-700 border-slate-600 text-white'
                  : 'bg-white border-slate-300 text-slate-900'
              }`}
            >
              <option value="vacation">{t('timeOff.vacation')}</option>
              <option value="sick">{t('timeOff.sickLeave')}</option>
              <option value="personal">{t('timeOff.personal')}</option>
              <option value="emergency">{t('timeOff.emergency')}</option>
            </select>
          </div>

          {/* Start Date */}
          <div>
            <label
              className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}
            >
              {t('timeOff.startDate')}
            </label>
            <input
              type="date"
              value={formData.start_date}
              onChange={(e) =>
                setFormData({ ...formData, start_date: e.target.value })
              }
              className={`w-full px-3 py-2 border rounded-lg ${
                isDark
                  ? 'bg-slate-700 border-slate-600 text-white'
                  : 'bg-white border-slate-300 text-slate-900'
              }`}
              required
            />
          </div>

          {/* End Date */}
          <div>
            <label
              className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}
            >
              {t('timeOff.endDate')}
            </label>
            <input
              type="date"
              value={formData.end_date}
              onChange={(e) =>
                setFormData({ ...formData, end_date: e.target.value })
              }
              className={`w-full px-3 py-2 border rounded-lg ${
                isDark
                  ? 'bg-slate-700 border-slate-600 text-white'
                  : 'bg-white border-slate-300 text-slate-900'
              }`}
              required
            />
          </div>

          {/* Overlap Warning */}
          {formData.start_date && formData.end_date && hasOverlap(new Date(formData.start_date), new Date(formData.end_date)) && (
            <div
              className={`px-3 py-2 border rounded-lg text-sm ${
                isDark
                  ? 'bg-yellow-900/20 border-yellow-700 text-yellow-300'
                  : 'bg-yellow-50 border-yellow-200 text-yellow-800'
              }`}
            >
              You have an existing request overlapping these dates
            </div>
          )}

          {/* Reason */}
          <div>
            <label
              className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}
            >
              {t('timeOff.reason')}
            </label>
            <textarea
              value={formData.reason}
              onChange={(e) =>
                setFormData({ ...formData, reason: e.target.value })
              }
              placeholder={t('timeOff.reasonPlaceholder')}
              className={`w-full px-3 py-2 border rounded-lg ${
                isDark
                  ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400'
                  : 'bg-white border-slate-300 text-slate-900 placeholder-slate-500'
              }`}
              rows={3}
            />
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className={`flex-1 px-4 py-2 rounded-lg font-medium transition-all ${
                isDark
                  ? 'bg-slate-700 text-white hover:bg-slate-600'
                  : 'bg-slate-200 text-slate-900 hover:bg-slate-300'
              } disabled:opacity-50`}
            >
              {t('timeOff.cancel')}
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 disabled:opacity-50 transition-all"
            >
              {requestToEdit
                ? (submitting ? 'Updating...' : 'Update Request')
                : (submitting ? t('timeOff.submitting') : t('timeOff.submitRequest'))}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TimeOffRequestModal;
